import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  runPhase10GateA,
  runPhase10GateBPreflight,
  setCheckoutMode,
  capturePhase10Snapshot,
  authorizeLiveCheckout,
  emergencyCloseCheckout,
  getPhase10Status,
} from "@/lib/phase10.functions";

export const Route = createFileRoute("/_authenticated/admin/bkls/phase10")({
  head: () => ({
    meta: [
      { title: "Phase 10 Activation · Buddha KA" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Phase10Console,
});

const card = "rounded-2xl border hairline bg-charcoal p-6";
const TONE: Record<string, string> = {
  pass: "border-emerald/40 text-emerald",
  warn: "border-gold/50 text-gold",
  fail: "border-destructive/50 text-destructive",
};

function Pill({ status }: { status: string }) {
  return (
    <span
      className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${TONE[status] ?? "hairline text-muted-foreground"}`}
    >
      {status}
    </span>
  );
}

type Check = { key: string; label: string; status: string; detail: string; action?: string };

function CheckList({ checks }: { checks: Check[] }) {
  return (
    <ul className="mt-4 grid gap-3 sm:grid-cols-2">
      {checks.map((c) => (
        <li key={c.key} className="rounded-xl border hairline bg-obsidian p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm text-ivory">{c.label}</p>
            <Pill status={c.status} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{c.detail}</p>
          {c.action && <p className="mt-1 text-xs text-gold">{c.action}</p>}
        </li>
      ))}
    </ul>
  );
}

function Phase10Console() {
  const qc = useQueryClient();
  const gateA = useServerFn(runPhase10GateA);
  const preflight = useServerFn(runPhase10GateBPreflight);
  const setMode = useServerFn(setCheckoutMode);
  const snapshot = useServerFn(capturePhase10Snapshot);
  const authorize = useServerFn(authorizeLiveCheckout);
  const rollback = useServerFn(emergencyCloseCheckout);
  const statusFn = useServerFn(getPhase10Status);

  const [phrase, setPhrase] = useState("");
  const [relaunchAck, setRelaunchAck] = useState("");
  const [relaunchReason, setRelaunchReason] = useState("");
  const [message, setMessage] = useState<string | null>(null);


  const statusQuery = useQuery({ queryKey: ["phase10", "status"], queryFn: () => statusFn() });
  const statusData = statusQuery.data as any;
  // A refused/failed status read must not render as a partial posture.
  const status = statusData && !statusData.error ? statusData : null;
  const statusError = statusData?.error as string | undefined;
  const gateAQuery = useQuery({ queryKey: ["phase10", "gateA"], queryFn: () => gateA() });
  const preflightMutation = useMutation({
    mutationFn: () => preflight(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["phase10", "status"] }),
  });
  const snapshotMutation = useMutation({
    mutationFn: () => snapshot(),
    onSuccess: (r: any) => {
      if (r && "error" in r) setMessage(r.error);
      qc.invalidateQueries({ queryKey: ["phase10", "status"] });
    },
  });

  const modeMutation = useMutation({
    mutationFn: (mode: "sandbox" | "live") => setMode({ data: { mode } }),
    onSuccess: (r: any) => {
      setMessage("error" in r ? r.error : `Checkout mode set to ${r.mode}; checkout remains closed.`);
      qc.invalidateQueries({ queryKey: ["phase10"] });
    },
  });

  const authorizeMutation = useMutation({
    mutationFn: () =>
      authorize({
        data: {
          phrase,
          relaunchAcknowledgement: relaunchAck.trim() || undefined,
          relaunchReason: relaunchReason.trim() || undefined,
        },
      }),

    onSuccess: (r: any) => {
      setMessage("error" in r ? r.error : "Live checkout is OPEN.");
      qc.invalidateQueries({ queryKey: ["phase10"] });
    },
  });

  const rollbackMutation = useMutation({
    mutationFn: () => rollback({ data: { reason: "Emergency rollback from Phase 10 console" } }),
    onSuccess: (r: any) => {
      setMessage("error" in r ? r.error : "Checkout closed. Catalog remains public.");
      qc.invalidateQueries({ queryKey: ["phase10"] });
    },
  });

  const gateAData = gateAQuery.data as any;
  const gateAError = gateAData?.error as string | undefined;
  const report = (gateAError ? undefined : gateAData) as
    | { passed: boolean; failedCount: number; warningCount: number; checks: Check[] }
    | undefined;


  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Phase 10</p>
          <h1 className="mt-2 font-serif text-3xl">Live activation gates.</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Gate A verifies live infrastructure without touching money. Gate B opens revenue only through explicit
            authorization, and can be rolled back without hiding the catalog.
          </p>
        </div>
        <Link to="/admin/bkls/readiness" className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold">
          ← Readiness
        </Link>
      </header>

      {message && <p className="rounded-xl border hairline bg-obsidian p-4 text-sm text-gold">{message}</p>}

      <section className={card}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-xl">Server-derived activation status</h2>
          <span className="rounded-full border border-gold/50 px-4 py-1 text-[10px] uppercase tracking-[0.25em] text-gold">
            {status?.status ?? (statusQuery.isLoading ? "Loading…" : "Unknown")}
          </span>
        </div>
        {statusError && <p className="mt-4 text-sm text-destructive">{statusError}</p>}
        {status && (
          <>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ["public_enabled", String(status.settings.public_enabled)],
                ["checkout_enabled", String(status.settings.checkout_enabled)],
                ["checkout_mode", String(status.settings.checkout_mode)],
                ["Gate A passed", status.evidence.gateAPassedAt ?? "never"],
                ["Gate A expires", status.evidence.gateAExpiresAt ?? "—"],
                [
                  "Gate A fingerprint",
                  status.evidence.gateAFingerprintMatches === null
                    ? "—"
                    : status.evidence.gateAFingerprintMatches
                      ? "matches"
                      : "MISMATCH",
                ],
                ["Mode changed", status.evidence.modeChangedAt ?? "never"],
                ["Live preflight", status.evidence.preflightPassedAt ?? "never"],
                [
                  "Preflight fingerprint",
                  status.evidence.preflightFingerprintMatches === null
                    ? "—"
                    : status.evidence.preflightFingerprintMatches
                      ? "matches"
                      : "MISMATCH",
                ],
                ["Snapshot", status.evidence.snapshotCapturedAt ?? "never"],
                ["Snapshot ID", status.evidence.snapshotId ?? "—"],
                ["Snapshot hash", status.evidence.snapshotHash?.slice(0, 16) ?? "—"],
                ["Config fingerprint", status.configurationFingerprint],
                ["Invalidation", status.evidence.invalidationReason ?? "none"],
                ["Authorized at", status.evidence.authorizedAt ?? "not authorized"],
                ["Live orders / licences", `${status.liveOrderCount} / ${status.liveLicenceCount}`],
              ].map(([label, value]) => (
                <div key={label as string} className="rounded-xl border hairline bg-obsidian p-3">
                  <dt className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</dt>
                  <dd className="mt-1 break-all text-xs text-ivory">{value}</dd>
                </div>
              ))}
            </dl>
            {!status.eligible && status.refusals?.length > 0 && (
              <ul className="mt-4 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                {status.refusals.map((r: string) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>



      <section className={card}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-xl">Gate A · Live infrastructure preparation</h2>
          <button
            type="button"
            onClick={() => gateAQuery.refetch().then(() => qc.invalidateQueries({ queryKey: ["phase10", "status"] }))}
            className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold"
          >
            {gateAQuery.isFetching ? "Verifying…" : "Re-run Gate A"}
          </button>
        </div>
        {gateAQuery.isLoading && <p className="mt-4 text-sm text-muted-foreground">Running live verification…</p>}
        {gateAQuery.error && <p className="mt-4 text-sm text-destructive">{(gateAQuery.error as Error).message}</p>}
        {gateAError && <p className="mt-4 text-sm text-destructive">{gateAError}</p>}
        {report && (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              {report.passed ? "All Gate A checks pass" : `${report.failedCount} blocking issue(s)`} ·{" "}
              {report.warningCount} warning(s)
            </p>
            <CheckList checks={report.checks} />
          </>
        )}
      </section>

      <section className={card}>
        <h2 className="font-serif text-xl">Gate B · Controlled launch authorization</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Available only after Gate A passes. Each step is audited.
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!report?.passed || modeMutation.isPending}
            onClick={() => modeMutation.mutate("live")}
            className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold disabled:opacity-40"
          >
            1 · Switch mode to live (checkout stays closed)
          </button>
          <button
            type="button"
            disabled={preflightMutation.isPending || status?.settings?.checkout_mode !== "live" || !status?.evidence?.gateAPassedAt}
            onClick={() => preflightMutation.mutate()}
            className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold disabled:opacity-40"
          >
            2 · No-charge live preflight
          </button>
          <button
            type="button"
            disabled={snapshotMutation.isPending || !status?.evidence?.preflightPassedAt}
            onClick={() => snapshotMutation.mutate()}
            className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold disabled:opacity-40"
          >
            3 · Capture pre-activation snapshot
          </button>
        </div>

        {preflightMutation.data && <CheckList checks={(preflightMutation.data as any).checks} />}
        {snapshotMutation.data && (
          <pre className="mt-4 overflow-x-auto rounded-xl border hairline bg-obsidian p-4 text-[11px] text-muted-foreground">
            {JSON.stringify(snapshotMutation.data, null, 2)}
          </pre>
        )}

        <div className="mt-6 rounded-xl border hairline bg-obsidian p-4">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold">4 · Final release authorization</p>
          <p className="mt-2 text-xs text-muted-foreground">
            Type <span className="text-ivory">AUTHORIZE PHASE 10 LIVE CHECKOUT</span> to open live checkout.
          </p>
          {status?.relaunchRequired && (
            <div className="mt-3 space-y-2 rounded-xl border border-gold/40 p-3">
              <p className="text-xs text-gold">
                Live records already exist. This is a relaunch: type{" "}
                <span className="text-ivory">RELAUNCH AFTER RESOLVED INCIDENT</span> and record why it is safe.
              </p>
              <input
                value={relaunchAck}
                onChange={(e) => setRelaunchAck(e.target.value)}
                aria-label="Relaunch acknowledgement"
                className="w-full rounded-full border hairline bg-charcoal px-4 py-2 text-sm"
                placeholder="Relaunch acknowledgement"
              />
              <input
                value={relaunchReason}
                onChange={(e) => setRelaunchReason(e.target.value)}
                aria-label="Relaunch reason"
                className="w-full rounded-full border hairline bg-charcoal px-4 py-2 text-sm"
                placeholder="Reason recorded in the audit log"
              />
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              aria-label="Release authorization phrase"
              className="min-w-[280px] flex-1 rounded-full border hairline bg-charcoal px-4 py-2 text-sm"
              placeholder="Authorization phrase"
            />
            <button
              type="button"
              disabled={
                authorizeMutation.isPending ||
                !phrase.trim() ||
                !(status?.eligible || status?.eligibleWithRelaunch) ||
                (status?.relaunchRequired && (!relaunchAck.trim() || relaunchReason.trim().length < 10))
              }
              onClick={() => authorizeMutation.mutate()}
              className="rounded-full bg-gold-gradient px-5 py-2 text-xs font-semibold text-obsidian disabled:opacity-40"
            >
              Open live checkout
            </button>
          </div>

        </div>

        <div className="mt-4 rounded-xl border border-destructive/40 bg-obsidian p-4">
          <p className="text-[10px] uppercase tracking-[0.25em] text-destructive">Emergency rollback</p>
          <p className="mt-2 text-xs text-muted-foreground">
            Closes checkout immediately. The catalog stays public and licences already issued are untouched.
          </p>
          <button
            type="button"
            disabled={rollbackMutation.isPending}
            onClick={() => rollbackMutation.mutate()}
            className="mt-3 rounded-full border border-destructive/50 px-4 py-2 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-40"
          >
            Close checkout now
          </button>
        </div>
      </section>
    </div>
  );
}
